import { format } from 'date-fns';
import { RECEIVED_STATUSES } from '@/constants/manifestStatuses';
import { EXCEPTION_TYPES } from '@/constants/exceptionConstants';
import orderStatusStrings from '@/constants/orderStatusStrings';
import { isSplitChild } from '@/components/OrderDetailsPage/utilities/splitOrders';
import { DISPOSITION_LABELS } from '@/components/ShipmentForm/constants';
import { formatHHMM } from '@/utilities/hhmm';
import { CLAIM_STATUSES, CLAIM_TYPES } from '@/constants/claims';
import {
    BOARD_MODES,
    COLUMN_EMPTY_ERROR,
    COLUMN_NEEDS_PHYSICAL_ERROR,
    ITEM_TAG_KEYS,
    ITEM_TAG_LABELS,
    GENERIC_SPLIT_ERROR,
    UNASSIGNED_TITLE,
} from './constants';

const RESOLVED = 'RESOLVED';

const normalizeType = (type) => String(type || '').toLowerCase();

/** The exception's type: `type` from the exceptions fragment, with `exception_type` as a fallback. */
const typeOf = (exception) => exception?.type ?? exception?.exception_type;

const isShortageType = (type) => normalizeType(type) === 'shortage';

/** Readable label for an exception type such as `itemDamaged` or `DAMAGED`. */
const exceptionLabel = (type) => {
    const match = Object.entries(EXCEPTION_TYPES).find(([key]) => normalizeType(key) === normalizeType(type));
    if (match) return match[1].label;
    const spaced = String(type || '')
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .replace(/[_-]+/g, ' ')
        .toLowerCase();
    return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

/**
 * Work out the tag that tells a user whether an item line has arrived.
 * Order is open shortage, then received, then any other open exception, then not received. A resolved exception is ignored.
 *
 * @param {{ pallet?: { warehouse_status?: string }, exceptions?: Array<{ exception?: { type?: string, exception_type?: string, status?: string } }> }} item Item line from the order query.
 * @returns {{ key: 'received'|'shortage'|'exception'|'notReceived', label: string }} Tag key and display text.
 */
export function itemStatusTag(item) {
    const open = (item?.exceptions || [])
        .map((mapping) => mapping?.exception)
        .filter((exception) => exception && exception.status !== RESOLVED);

    if (open.some((exception) => isShortageType(typeOf(exception)))) {
        return { key: ITEM_TAG_KEYS.SHORTAGE, label: ITEM_TAG_LABELS[ITEM_TAG_KEYS.SHORTAGE] };
    }
    if (RECEIVED_STATUSES.includes(item?.pallet?.warehouse_status)) {
        return { key: ITEM_TAG_KEYS.RECEIVED, label: ITEM_TAG_LABELS[ITEM_TAG_KEYS.RECEIVED] };
    }
    if (open.length > 0) {
        return { key: ITEM_TAG_KEYS.EXCEPTION, label: exceptionLabel(typeOf(open[0])) };
    }
    return { key: ITEM_TAG_KEYS.NOT_RECEIVED, label: ITEM_TAG_LABELS[ITEM_TAG_KEYS.NOT_RECEIVED] };
}

/**
 * The distinct open item-exception tags across a list of items, in first-seen order. An exception is open when its
 * status is not RESOLVED. Types compare case-insensitively, so `SHORTAGE` and `shortage` make one tag. Order-level
 * exceptions are not part of an item and are never included.
 *
 * @param {Array<{ exceptions?: Array<{ exception?: { type?: string, exception_type?: string, status?: string } }> }>} [items] Item lines with their exception mappings.
 * @returns {Array<{ key: string, label: string }>} One entry per open exception type. `key` is the lower-case type.
 */
export function openItemExceptionTags(items) {
    const tags = new Map();
    (items || []).forEach((item) => {
        (item?.exceptions || []).forEach((mapping) => {
            const exception = mapping?.exception;
            if (!exception || exception.status === RESOLVED) return;
            const type = typeOf(exception);
            const key = normalizeType(type);
            if (!key || tags.has(key)) return;
            const label = isShortageType(type) ? ITEM_TAG_LABELS[ITEM_TAG_KEYS.SHORTAGE] : exceptionLabel(type);
            tags.set(key, { key, label });
        });
    });
    return [...tags.values()];
}

const isTangible = (item) => !item?.intangible;

/**
 * Build the `split_order` variables, one child per new order in column order.
 *
 * Each child is `{ items, services, claims, shipper_billable }`. A line held whole by one
 * child is sent as `{ item_id }`; a line divided across children is sent as `{ item_id, quantity }` in each child
 * holding part of it. `services` lists the `service_id`s placed on that child and is always present (empty when
 * the parent has none), so the server moves services instead of deleting them. `claims` lists the `claim_id`s of
 * the open claims placed on that child and is always present (empty when none); the server requires every open
 * claim on the parent to be listed under exactly one child. `shipper_billable` is the column's Billable switch.
 *
 * @param {{ orderId: string, orderStatus: string, items: Array<{ item_id: string, quantity?: number|string|null }>, layout: SplitLayout, services?: Array<{ service_id: string }>, claims?: Array<{ claim_id: string }> }} args
 *   `orderStatus` is the parent's current `order_status`, passed on as the children's starting status. `items`,
 *   `services` and `claims` are the parent's rows from the order read; services and claims follow that order within
 *   each child.
 * @returns {{ p_order_id: string, p_children: object[], p_child_status: string }} Mutation variables.
 */
export function buildSplitVariables({ orderId, orderStatus, items, layout, services, claims }) {
    return {
        p_order_id: orderId,
        p_children: layoutChildren(layout, items, services, claims),
        p_child_status: orderStatus,
    };
}

/**
 * Pick the parent and children out of a `split_order` response. Children are the rows with `split_seq` set, in
 * `split_seq` order. The parent is the row without one, even when it is itself a clone.
 *
 * @param {object[]|null|undefined} orders The `split_order` rows.
 * @returns {{ parent: object|null, children: object[] }} Parent order and the new child orders.
 */
export function readSplitResult(orders) {
    const rows = orders || [];
    return {
        parent: rows.find((order) => !isSplitChild(order)) || null,
        children: rows.filter(isSplitChild).sort((a, b) => a.split_seq - b.split_seq),
    };
}

/**
 * The success notice after a split, naming the new orders from the `split_order` response, for example
 * `Split into OD104409-1 and OD104409-2.` Three or more read `Split into A, B, and C.`
 *
 * @param {Array<{ order_number?: string }>|null|undefined} children The new child orders, in `split_seq` order (`readSplitResult`).
 * @returns {string} The message, or `Order split.` when no child has an order number.
 */
export function splitSuccessMessage(children) {
    const numbers = (children || []).map((child) => child?.order_number).filter(Boolean);
    if (numbers.length === 0) return 'Order split.';
    if (numbers.length <= 2) return `Split into ${numbers.join(' and ')}.`;
    return `Split into ${numbers.slice(0, -1).join(', ')}, and ${numbers[numbers.length - 1]}.`;
}

/** Reason text for each `split_blockers` code that disables the Split Order button. `{status}` is the status label. */
export const BLOCKER_REASONS = {
    ROUTED: 'This order is on a route. Take it off the route to split it.',
    INVOICED: "This order has been invoiced, so it can't be split.",
    UNCONFIRMED: "This order hasn't been confirmed yet, so it can't be split.",
    STATUS_NOT_SPLITTABLE: "{article} {status} order can't be split.",
    FORWARD_STOCKED: "This order is forward-stocked, so it can't be split.",
};

/** `STATUS_NOT_SPLITTABLE` reason for an order with no `order_status`. */
export const NO_STATUS_REASON = "This order has no status, so it can't be split.";

/** Reason shown when the order has too few item lines to split. */
export const LINE_COUNT_REASON = 'This order needs at least two item lines to split.';

/**
 * Turn `split_blockers` codes into one reason line each, in the order given. `ALREADY_SPLIT`, `IS_SPLIT_CHILD`, and `NOT_ENABLED`
 * hide the button instead, so they give no line. An unknown code gives no line either.
 *
 * @param {string[]|null|undefined} blockers Codes from `orders.split_blockers`.
 * @param {string} [orderStatus] The order's `order_status`, used for the status label in `STATUS_NOT_SPLITTABLE`.
 * @returns {string[]} Reason lines, one per known code.
 */
export function blockerReasons(blockers, orderStatus) {
    const label = orderStatusStrings[orderStatus] || orderStatus;
    const article = /^[aeiou]/i.test(label || '') ? 'An' : 'A';
    return (blockers || [])
        .filter((code) => BLOCKER_REASONS[code])
        .map((code) =>
            code === 'STATUS_NOT_SPLITTABLE' && !label
                ? NO_STATUS_REASON
                : BLOCKER_REASONS[code].replace('{article}', article).replace('{status}', label)
        );
}

/**
 * The line-count rule: an order needs at least two item lines and at least two tangible lines to split.
 *
 * @param {Array<{ intangible?: boolean }>|null|undefined} items Item lines.
 * @returns {string|null} The reason text, or null when the order has enough lines.
 */
export function lineCountReason(items) {
    const list = items || [];
    return list.length < 2 || list.filter(isTangible).length < 2 ? LINE_COUNT_REASON : null;
}

/**
 * One line per service on the order, for the board's service cards, in the form the split's
 * activity log uses: `1 × Used Merchandise Haul Away (10488)`. Falls back to the code when there is no description.
 *
 * @param {Array<{ code?: string|null, description?: string|null, quantity?: number|string|null }>|null|undefined} services
 *   Service lines from the modal's read.
 * @returns {string[]} The lines, empty when the order has no services.
 */
export function serviceLines(services) {
    return (services || []).map(({ code, description, quantity }) => {
        const qty = Number(quantity ?? 1);
        const name = description?.trim()
            ? `${description.trim()}${code?.trim() ? ` (${code.trim()})` : ''}`
            : code?.trim() || 'Service';
        return `${qty} × ${name}`;
    });
}

const yesNo = (value) => (value ? 'Yes' : 'No');

const blank = (value) => value == null || String(value).trim() === '';

const fixed2 = (value) => (blank(value) || !Number.isFinite(Number(value)) ? null : Number(value).toFixed(2));

/**
 * An item line's full name for tooltips and the details dialog title: SKU and description joined with an em dash,
 * or whichever one it has.
 *
 * @param {{ sku?: string|null, description?: string|null }} item Item line from the order read.
 * @returns {string} For example "CHR-DIN-OAK — Oak dining chair". "Item" when it has neither.
 */
export function itemFullName(item) {
    const parts = [item?.sku, item?.description].map((part) => part?.trim()).filter(Boolean);
    return parts.length ? parts.join(' — ') : 'Item';
}

/**
 * Label and value rows for an item card's details dialog, using the same labels and units as the Order Details
 * items table. Empty fields are left out.
 *
 * @param {object} item Item line from the order read.
 * @param {{ lineNumber: number, quantity?: number }} card `quantity` is this card's portion; it gets its own row
 *   when the line is divided.
 * @returns {Array<{ label: string, value: string }>} Rows in display order.
 */
export function itemDetailRows(item, { lineNumber, quantity } = {}) {
    const dims = [
        [item?.length, 'L'],
        [item?.width, 'W'],
        [item?.height, 'H'],
    ];
    const openExceptions = (item?.exceptions || [])
        .map((mapping) => mapping?.exception)
        .filter((exception) => exception && exception.status !== RESOLVED)
        .map((exception) => exceptionLabel(typeOf(exception)));
    const rows = [
        ['Line', lineNumber],
        ['SKU', item?.sku],
        ['Description', item?.description],
        ['Barcode', item?.barcode],
        ['Line ID', item?.line_id],
        ['Serial Number', item?.serial_number],
        ['Type', item?.item_type_details],
        ['Quantity', item?.quantity],
        ['On this card', quantity != null && Number(quantity) !== Number(item?.quantity) ? quantity : null],
        ['Status', itemStatusTag(item).label],
        ['Open exceptions', [...new Set(openExceptions)].join(', ')],
        ['Intangible', item?.intangible ? 'Yes' : null],
        [
            'Dimensions (in)',
            dims.some(([value]) => !blank(value) && Number(value))
                ? dims.map(([value, label]) => `${parseInt(value, 10) || 0}${label}`).join(' ')
                : null,
        ],
        ['Weight (lbs)', fixed2(item?.total_weight)],
        ['Cubes (cu ft)', fixed2(item?.total_cubes)],
        ['Disposition', item?.is_return ? DISPOSITION_LABELS[item.disposition] || 'Unknown' : null],
    ];
    return rows.filter(([, value]) => !blank(value)).map(([label, value]) => ({ label, value: String(value) }));
}

/**
 * Label and value rows for a service card's details dialog. Empty fields are left out; service time is shown as
 * HH:MM, as on the Services tab.
 *
 * @param {object} service Service line from the order read.
 * @returns {Array<{ label: string, value: string }>} Rows in display order.
 */
export function serviceDetailRows(service) {
    const rows = [
        ['Description', service?.description],
        ['Code', service?.code],
        ['Type', service?.type],
        ['Quantity', service?.quantity],
        ['Size', service?.size],
        ['Billable', service?.billable == null ? null : yesNo(service.billable)],
        ['Needs confirmation', service?.require_confirm ? 'Yes' : null],
        ['Confirmed', service?.require_confirm ? yesNo(service.confirmed) : null],
        ['Confirmed quantity', service?.confirmed ? service.confirmed_quantity : null],
        ['Service time', Number.isFinite(service?.service_time) ? formatHHMM(service.service_time) : null],
    ];
    return rows.filter(([, value]) => !blank(value)).map(([label, value]) => ({ label, value: String(value) }));
}

/** Readable label for a claim's `claim_type` or `claim_status`, from the Claims page's labels. */
const claimLabel = (labels, value) => (blank(value) ? null : labels[value] || String(value));

/**
 * A claim's freight amount as dollars, for example `$125.00`.
 *
 * @param {{ freight_amount?: number|string|null }} claim Claim from the order read.
 * @returns {string|null} The amount, or null when the claim has none.
 */
export function claimAmount(claim) {
    const amount = fixed2(claim?.freight_amount);
    return amount === null ? null : `$${amount}`;
}

/**
 * A claim card's name for its title, tooltip, and screen readers: the claim number and its type, for example
 * `CL1042 · Freight`.
 *
 * @param {{ claim_number?: string|null, claim_type?: string|null }} claim Claim from the order read.
 * @returns {string} The name. "Claim" stands in for a missing number.
 */
export function claimName(claim) {
    const number = claim?.claim_number?.trim() || 'Claim';
    const type = claimLabel(CLAIM_TYPES, claim?.claim_type);
    return type ? `${number} · ${type}` : number;
}

/**
 * The label for a claim's status, as the Claims page shows it (`ACCEPTED` reads "Approved").
 *
 * @param {{ claim_status?: string|null }} claim Claim from the order read.
 * @returns {string|null} The label, or null when the claim has no status.
 */
export function claimStatusLabel(claim) {
    return claimLabel(CLAIM_STATUSES, claim?.claim_status);
}

/**
 * Label and value rows for a claim card's details dialog. Empty fields are left out.
 *
 * @param {object} claim Claim from the order read.
 * @returns {Array<{ label: string, value: string }>} Rows in display order.
 */
export function claimDetailRows(claim) {
    const incident = claim?.incident_date ? new Date(claim.incident_date) : null;
    const rows = [
        ['Claim number', claim?.claim_number],
        ['Type', claimLabel(CLAIM_TYPES, claim?.claim_type)],
        ['Status', claimStatusLabel(claim)],
        ['Freight amount', claimAmount(claim)],
        ['Incident date', incident && !Number.isNaN(incident.getTime()) ? format(incident, 'MMM d, yyyy') : null],
    ];
    return rows.filter(([, value]) => !blank(value)).map(([label, value]) => ({ label, value: String(value) }));
}

/** `split_blockers` codes that hide the Split Order button instead of disabling it. */
const HIDING_BLOCKERS = ['ALREADY_SPLIT', 'IS_SPLIT_CHILD', 'NOT_ENABLED'];

/**
 * Work out how the Split Order button looks: hidden, or disabled with reason lines, or enabled.
 * `ALREADY_SPLIT`, `IS_SPLIT_CHILD`, and `NOT_ENABLED` (the shipper account lacks the `split-orders` flag) hide it. Blocker reasons come first, then the line-count reason.
 *
 * @param {{ blockers?: string[]|null, items?: Array<{ intangible?: boolean }>|null, orderStatus?: string }} args Data from the button's read.
 * @returns {{ hidden: boolean, reasons: string[] }} `reasons` is empty when the button is enabled.
 */
export function splitButtonState({ blockers, items, orderStatus } = {}) {
    if ((blockers || []).some((code) => HIDING_BLOCKERS.includes(code))) return { hidden: true, reasons: [] };
    const reasons = blockerReasons(blockers, orderStatus);
    const lines = lineCountReason(items);
    if (lines) reasons.push(lines);
    return { hidden: false, reasons };
}

/**
 * Sort a failed split into the text to show. A server (GraphQL) error shows its message verbatim, permission
 * errors included. Anything else is treated as a network error and gets the generic text.
 *
 * @param {{ message?: string, graphQLErrors?: Array<{ message: string }> }|null|undefined} error Error from the split call.
 * @returns {{ message: string, isNetwork: boolean }} Text for the alert, and whether it was a network error (log it).
 */
export function readSplitError(error) {
    const serverMessage = error?.graphQLErrors?.find((e) => e?.message)?.message;
    if (serverMessage) return { message: serverMessage, isNetwork: false };
    return { message: GENERIC_SPLIT_ERROR, isNetwork: true };
}

/** `invoicing_status` values that count as invoiced (same list as the server's `INVOICED` rule). */
const INVOICED_STATUSES = ['APPROVED', 'PRE_APPROVED'];

/**
 * Is this order invoiced, so its billing can't change? Mirrors the server: any invoice id (shipper, carrier,
 * middle-mile, warehouse), or an `invoicing_status` of `APPROVED` or `PRE_APPROVED`. The server is the authority.
 *
 * @param {{ shipper_invoice_id?: string|null, carrier_invoice_id?: string|null, middle_mile_carrier_invoice_id?: string|null, warehouse_carrier_invoice_id?: string|null, invoicing_status?: string|null }|null|undefined} order
 * @returns {boolean} True when the order is invoiced.
 */
export function isInvoiced(order) {
    if (!order) return false;
    const ids = [
        order.shipper_invoice_id,
        order.carrier_invoice_id,
        order.middle_mile_carrier_invoice_id,
        order.warehouse_carrier_invoice_id,
    ];
    return ids.some((id) => id != null && id !== '') || INVOICED_STATUSES.includes(order.invoicing_status);
}

/**
 * @typedef {object} LayoutOrder
 * @property {string} key Stable column key. Never an array index, so removing a column doesn't shuffle cards.
 * @property {boolean} billable Sent as the child's `shipper_billable`.
 */

/**
 * @typedef {object} LayoutLine
 * @property {string} item_id The parent item line this card belongs to.
 * @property {number} quantity How much of the line this card holds. The whole line's quantity until it is divided.
 * @property {string} portionKey Stable card key. Unique across the layout, even when a line is divided.
 * @property {string|null} orderKey Column the card is on, or null while it sits in the Unassigned tray.
 */

/**
 * @typedef {object} SplitLayout
 * @property {LayoutOrder[]} orders New-order columns, in the order they become children.
 * @property {LayoutLine[]} lines One entry per item card.
 * @property {Object<string, string>} serviceMap `service_id` to the column key holding it. No entry means the service is in the tray.
 * @property {Object<string, string>} claimMap `claim_id` to the column key holding it. No entry means the claim is in the tray.
 */

/** Column key for the nth new order a layout creates (1-based). */
const orderKeyFor = (n) => `order-${n}`;

const numericQuantity = (quantity) => Number(quantity ?? 1);

/**
 * The order number a new-order column will get: the parent's number plus `-1`, `-2`, … in column order.
 * Call it with the column's current index so labels renumber when a column is removed.
 *
 * @param {string} parentOrderNumber The parent's `order_number`, for example `OD104409`.
 * @param {number} index Zero-based column index.
 * @returns {string} For example `OD104409-1`.
 */
export function childLabel(parentOrderNumber, index) {
    return `${parentOrderNumber}-${index + 1}`;
}

/**
 * The layout the split board opens with: two empty billable orders, every item line whole in the tray,
 * and no services or claims placed.
 *
 * @param {Array<{ item_id: string, quantity?: number|string|null }>|null|undefined} items Item lines from the order read.
 * @param {Array<{ service_id: string }>|null|undefined} [services] The parent's services. All start in the tray.
 * @param {Array<{ claim_id: string }>|null|undefined} [claims] The parent's open claims. All start in the tray.
 * @returns {SplitLayout} The starting layout.
 */
export function initialLayout(items, services, claims) {
    return {
        orders: [
            { key: orderKeyFor(1), billable: true },
            { key: orderKeyFor(2), billable: true },
        ],
        lines: (items || []).map((item) => ({
            item_id: item.item_id,
            quantity: numericQuantity(item.quantity),
            portionKey: item.item_id,
            orderKey: null,
        })),
        serviceMap: {},
        claimMap: {},
    };
}

const pluralNoun = (n, noun) => `${n} ${noun}${n === 1 ? '' : 's'}`;

/**
 * What the Unassigned tray's header says in a board mode: how many cards are left and the prompt or done message.
 *
 * @param {SplitLayout} layout Current layout.
 * @param {'items'|'services'|'claims'} mode Board mode (`BOARD_MODES`).
 * @param {Array<{ service_id: string }>|null|undefined} [services] The parent's services, counted in services mode.
 * @param {Array<{ claim_id: string }>|null|undefined} [claims] The parent's open claims, counted in claims mode.
 * @returns {{ count: number, countText: string, message: string, clear: boolean }} `clear` is true once the tray is empty.
 */
export function trayStatus(layout, mode, services, claims) {
    let noun = 'item';
    let count = (layout?.lines || []).filter((line) => line.orderKey == null).length;
    if (mode === BOARD_MODES.SERVICES) {
        noun = 'service';
        count = (services || []).filter((service) => !layout?.serviceMap?.[service.service_id]).length;
    } else if (mode === BOARD_MODES.CLAIMS) {
        noun = 'claim';
        count = (claims || []).filter((claim) => !layout?.claimMap?.[claim.claim_id]).length;
    }
    return {
        count,
        countText: pluralNoun(count, noun),
        message: count > 0 ? `Drag each ${noun} onto an order` : `All ${noun}s assigned`,
        clear: count === 0,
    };
}

/**
 * Header numbers for one new-order column: its item cards, their total quantity, its services, and its claims.
 *
 * @param {SplitLayout} layout Current layout.
 * @param {string} orderKey The column's key.
 * @returns {{ lineCount: number, quantity: number, serviceCount: number, claimCount: number, text: string, itemText: string, serviceText: string, claimText: string }}
 *   `text` reads like `2 lines · qty 7 · 1 service`; `itemText` is its item part (`2 lines · qty 7`) and `serviceText`
 *   its service part (`1 service`), for services mode. `claimText` (`1 claim`) is the header in claims mode.
 */
export function columnSummary(layout, orderKey) {
    const lines = (layout?.lines || []).filter((line) => line.orderKey === orderKey);
    // Rounded so non-whole quantities don't show float noise such as 0.30000000000000004.
    const quantity = Math.round(lines.reduce((total, line) => total + line.quantity, 0) * 1e4) / 1e4;
    const serviceCount = Object.values(layout?.serviceMap || {}).filter((key) => key === orderKey).length;
    const itemText = `${pluralNoun(lines.length, 'line')} · qty ${quantity}`;
    const serviceText = pluralNoun(serviceCount, 'service');
    const claimCount = Object.values(layout?.claimMap || {}).filter((key) => key === orderKey).length;
    return {
        lineCount: lines.length,
        quantity,
        serviceCount,
        claimCount,
        text: `${itemText} · ${serviceText}`,
        itemText,
        serviceText,
        claimText: pluralNoun(claimCount, 'claim'),
    };
}

/**
 * Item ids whose line is currently divided into more than one card, so each card can say which line it is part of.
 *
 * @param {SplitLayout} layout Current layout.
 * @returns {Set<string>} Ids with two or more portions.
 */
export function dividedItemIds(layout) {
    const counts = new Map();
    (layout?.lines || []).forEach((line) => counts.set(line.item_id, (counts.get(line.item_id) || 0) + 1));
    return new Set([...counts].filter(([, n]) => n > 1).map(([id]) => id));
}

/**
 * @typedef {object} LineMove
 * @property {string} portionKey The card to move.
 * @property {string|null} toOrderKey Destination column key, or null for the Unassigned tray.
 * @property {string|null} [beforePortionKey] Card to place it in front of. Omitted or null puts it last.
 */

/**
 * Move one item card to a column or back to the tray, keeping the rest of the layout as is.
 * An unknown card or column leaves the layout unchanged.
 *
 * @param {SplitLayout} layout Current layout.
 * @param {LineMove} move Where the card goes.
 * @returns {SplitLayout} A new layout, or the same one when the move doesn't apply.
 */
export function moveLine(layout, { portionKey, toOrderKey, beforePortionKey = null }) {
    const line = layout.lines.find((l) => l.portionKey === portionKey);
    const target = toOrderKey ?? null;
    if (!line) return layout;
    if (target !== null && !layout.orders.some((order) => order.key === target)) return layout;
    const rest = layout.lines.filter((l) => l !== line);
    const moved = { ...line, orderKey: target };
    return mergePortions({ ...layout, lines: insertLine(rest, moved, beforePortionKey) });
}

/** Put a card in front of `beforePortionKey`, or last when that card isn't there. */
function insertLine(lines, line, beforePortionKey) {
    const at = beforePortionKey ? lines.findIndex((l) => l.portionKey === beforePortionKey) : -1;
    return at === -1 ? [...lines, line] : [...lines.slice(0, at), line, ...lines.slice(at)];
}

/**
 * Whether a card can be divided across orders: its quantity is a whole number of 2 or more, matching the server's
 * rule that each portion is a whole number of at least 1, and its line has no open item exception (the server
 * rejects a partial split of such a line). Other cards always move whole.
 *
 * @param {{ quantity: number }|null|undefined} line A layout card.
 * @param {{ exceptions?: Array<{ exception?: { type?: string, exception_type?: string, status?: string } }> }|null} [item]
 *   The card's item line from the order read, for its exceptions.
 * @returns {boolean} True when moving it should ask how many to move.
 */
export function canDivideLine(line, item = null) {
    const quantity = Number(line?.quantity);
    if (!Number.isInteger(quantity) || quantity < 2) return false;
    return !item || openItemExceptionTags([item]).length === 0;
}

/** A card key not used in `lines`, derived from the line's item id. */
function newPortionKey(lines, itemId) {
    const used = new Set(lines.map((line) => line.portionKey));
    let n = 2;
    while (used.has(`${itemId}~${n}`)) n += 1;
    return `${itemId}~${n}`;
}

/**
 * Move part of a card's quantity to a column or the tray, leaving the rest where it was. Moving the full quantity
 * moves the card whole (`moveLine`). Cards of the same line that end up in the same place merge, so a line's cards
 * always add up to its quantity. An unknown card or column, a move to the card's own place, a card that can't be
 * divided (`canDivideLine`) with less than its full quantity, or a quantity that isn't a whole number from 1 to the
 * card's quantity leaves the layout unchanged.
 *
 * @param {SplitLayout} layout Current layout.
 * @param {LineMove & { quantity: number }} move The card, where its part goes, and how much of it to move.
 * @param {object|null} [item] The card's item line from the order read, so a line with an open item exception
 *   only moves whole.
 * @returns {SplitLayout} A new layout, or the same one when the move doesn't apply.
 */
export function divideLine(layout, { portionKey, toOrderKey, quantity, beforePortionKey = null }, item = null) {
    const line = layout.lines.find((l) => l.portionKey === portionKey);
    const target = toOrderKey ?? null;
    const amount = Number(quantity);
    if (!line || (line.orderKey ?? null) === target) return layout;
    if (target !== null && !layout.orders.some((order) => order.key === target)) return layout;
    if (amount === line.quantity) return moveLine(layout, { portionKey, toOrderKey: target, beforePortionKey });
    if (!canDivideLine(line, item) || !Number.isInteger(amount) || amount < 1 || amount > line.quantity) return layout;
    const part = {
        item_id: line.item_id,
        quantity: amount,
        portionKey: newPortionKey(layout.lines, line.item_id),
        orderKey: target,
    };
    const lines = layout.lines.map((l) => (l === line ? { ...l, quantity: roundQuantity(l.quantity - amount) } : l));
    return mergePortions({ ...layout, lines: insertLine(lines, part, beforePortionKey) });
}

/**
 * Turn a `react-beautiful-dnd` drop result into a card move. The tray's drop id is `TRAY_DROPPABLE_ID`; each
 * column's is its layout key. Returns null for a drop outside any target or back on the spot it started from.
 *
 * @param {SplitLayout} layout The layout the board is showing, so the drop index lines up with the visible cards.
 * @param {{ draggableId: string, source?: { droppableId: string, index: number }, destination?: { droppableId: string, index: number }|null }} result Drop result.
 * @param {string} trayId The tray's drop id.
 * @returns {LineMove|null} The move to apply.
 */
export function moveFromDrop(layout, result, trayId) {
    const { draggableId, source, destination } = result || {};
    if (!destination) return null;
    if (source && source.droppableId === destination.droppableId && source.index === destination.index) return null;
    const toOrderKey = destination.droppableId === trayId ? null : destination.droppableId;
    const others = layout.lines.filter(
        (line) => (line.orderKey ?? null) === toOrderKey && line.portionKey !== draggableId
    );
    return {
        portionKey: draggableId,
        toOrderKey,
        beforePortionKey: others[destination.index]?.portionKey ?? null,
    };
}

/**
 * The places a card can be sent from its Move to menu: the tray, then each column by its label, in column order.
 *
 * @param {SplitLayout} layout Current layout.
 * @param {string} parentOrderNumber The parent's `order_number`, for column labels.
 * @returns {Array<{ key: string|null, label: string }>} `key` is null for the tray.
 */
export function moveTargets(layout, parentOrderNumber) {
    return [
        { key: null, label: UNASSIGNED_TITLE },
        ...layout.orders.map((order, index) => ({ key: order.key, label: childLabel(parentOrderNumber, index) })),
    ];
}

/** The fewest new orders a split can make. */
export const MIN_NEW_ORDERS = 2;

const roundQuantity = (quantity) => Math.round(quantity * 1e4) / 1e4;

/**
 * Join cards of the same line that sit in the same place (the same column, or the tray) into one card with the
 * summed quantity. The first card keeps its key and position.
 *
 * @param {SplitLayout} layout Current layout.
 * @returns {SplitLayout} A new layout, or the same one when nothing needed merging.
 */
export function mergePortions(layout) {
    const first = new Map();
    const lines = [];
    let merged = false;
    layout.lines.forEach((line) => {
        const place = `${line.item_id}\u0000${line.orderKey ?? ''}`;
        const at = first.get(place);
        if (at === undefined) {
            first.set(place, lines.length);
            lines.push(line);
            return;
        }
        merged = true;
        lines[at] = { ...lines[at], quantity: roundQuantity(lines[at].quantity + line.quantity) };
    });
    return merged ? { ...layout, lines } : layout;
}

/**
 * Add an empty, billable new-order column at the end. Its key never matches a column still on the board.
 *
 * @param {SplitLayout} layout Current layout.
 * @returns {SplitLayout} A new layout with one more column.
 */
export function addOrder(layout) {
    const highest = layout.orders.reduce((max, order) => {
        const n = Number(String(order.key).replace(/^order-/, ''));
        return Number.isFinite(n) && n > max ? n : max;
    }, 0);
    return { ...layout, orders: [...layout.orders, { key: orderKeyFor(highest + 1), billable: true }] };
}

/**
 * Whether a column can be removed: only while there are more than `MIN_NEW_ORDERS`.
 *
 * @param {SplitLayout} layout Current layout.
 * @returns {boolean} True when the remove control should show.
 */
export function canRemoveOrder(layout) {
    return layout.orders.length > MIN_NEW_ORDERS;
}

/**
 * Remove a new-order column. Its item cards, services, and claims go back to the tray, and cards of the same line that meet
 * there merge. Labels follow column order, so the columns after it renumber. Does nothing for an unknown column or
 * when only `MIN_NEW_ORDERS` remain.
 *
 * @param {SplitLayout} layout Current layout.
 * @param {string} orderKey The column to remove.
 * @returns {SplitLayout} A new layout, or the same one when the column can't be removed.
 */
export function removeOrder(layout, orderKey) {
    if (!canRemoveOrder(layout) || !layout.orders.some((order) => order.key === orderKey)) return layout;
    const without = (map) => Object.fromEntries(Object.entries(map || {}).filter(([, key]) => key !== orderKey));
    return mergePortions({
        ...layout,
        orders: layout.orders.filter((order) => order.key !== orderKey),
        lines: layout.lines.map((line) => (line.orderKey === orderKey ? { ...line, orderKey: null } : line)),
        serviceMap: without(layout.serviceMap),
        claimMap: without(layout.claimMap),
    });
}

/**
 * Place one of the parent's services on a new-order column, or send it back to the tray with `null`. A service
 * always moves whole. An unknown column leaves the layout unchanged.
 *
 * @param {SplitLayout} layout Current layout.
 * @param {string} serviceId The service's `service_id`.
 * @param {string|null} orderKey Destination column key, or null for the Unassigned tray.
 * @returns {SplitLayout} A new layout, or the same one when nothing changes.
 */
export function assignService(layout, serviceId, orderKey) {
    return assignWhole(layout, 'serviceMap', serviceId, orderKey);
}

/**
 * Place one of the parent's open claims on a new-order column, or send it back to the tray with `null`. A claim
 * always moves whole. An unknown column leaves the layout unchanged.
 *
 * @param {SplitLayout} layout Current layout.
 * @param {string} claimId The claim's `claim_id`.
 * @param {string|null} orderKey Destination column key, or null for the Unassigned tray.
 * @returns {SplitLayout} A new layout, or the same one when nothing changes.
 */
export function assignClaim(layout, claimId, orderKey) {
    return assignWhole(layout, 'claimMap', claimId, orderKey);
}

/** Move one id in a whole-card map (`serviceMap` or `claimMap`). Used by `assignService` and `assignClaim`. */
function assignWhole(layout, mapKey, id, orderKey) {
    const target = orderKey ?? null;
    const current = layout[mapKey]?.[id] ?? null;
    if (!id || target === current) return layout;
    if (target !== null && !layout.orders.some((order) => order.key === target)) return layout;
    const rest = Object.fromEntries(Object.entries(layout[mapKey] || {}).filter(([key]) => key !== id));
    return { ...layout, [mapKey]: target === null ? rest : { ...rest, [id]: target } };
}

/**
 * @typedef {object} ModeBadge
 * @property {number} count The badge number: the parent's item lines, services, or open claims.
 * @property {boolean} warn True when that mode still has something to fix, which turns the badge orange.
 * @property {string} tooltip What's left, for example `3 items unassigned · 1 order needs a physical item`. Empty when `warn` is false.
 */

/**
 * The count badges on the board's Items | Services | Claims switch. Items warns while any item card is in the tray
 * or a new order has no physical item; Services warns while any service is in the tray, and Claims while any open
 * claim is. An empty order only counts once the tray has no items left, so the opening layout doesn't read as a list
 * of errors.
 *
 * @param {SplitLayout} layout Current layout.
 * @param {Array<{ item_id: string, intangible?: boolean }>|null|undefined} items The parent's item lines from the order read.
 * @param {Array<{ service_id: string }>|null|undefined} services The parent's services.
 * @param {Array<{ claim_id: string }>|null|undefined} [claims] The parent's open claims.
 * @returns {{ items: ModeBadge, services: ModeBadge, claims: ModeBadge }} One badge per mode, keyed by `BOARD_MODES` value.
 */
export function modeBadges(layout, items, services, claims) {
    const byId = new Map((items || []).map((item) => [item.item_id, item]));
    const lines = (layout?.lines || []).filter((line) => byId.has(line.item_id));
    const itemsLeft = lines.filter((line) => line.orderKey == null).length;
    const needPhysical = Object.keys(columnProblems(layout, items)).length;
    const itemTips = [
        itemsLeft > 0 ? `${pluralNoun(itemsLeft, 'item')} unassigned` : '',
        needPhysical > 0
            ? `${pluralNoun(needPhysical, 'order')} ${needPhysical === 1 ? 'needs' : 'need'} a physical item`
            : '',
    ].filter(Boolean);
    const servicesLeft = trayStatus(layout, BOARD_MODES.SERVICES, services).count;
    const claimsLeft = trayStatus(layout, BOARD_MODES.CLAIMS, services, claims).count;
    return {
        items: { count: (items || []).length, warn: itemTips.length > 0, tooltip: itemTips.join(' · ') },
        services: {
            count: (services || []).length,
            warn: servicesLeft > 0,
            tooltip: servicesLeft > 0 ? `${pluralNoun(servicesLeft, 'service')} unassigned` : '',
        },
        claims: {
            count: (claims || []).length,
            warn: claimsLeft > 0,
            tooltip: claimsLeft > 0 ? `${pluralNoun(claimsLeft, 'claim')} unassigned` : '',
        },
    };
}

/**
 * Set whether a new-order column is billable to the shipper (sent as that child's `shipper_billable`). Local only:
 * nothing is written until the split is submitted. An unknown column or an unchanged value leaves the layout as is.
 *
 * @param {SplitLayout} layout Current layout.
 * @param {string} orderKey The column's key.
 * @param {boolean} billable True to bill the shipper; false charges the shipper $0 for that order.
 * @returns {SplitLayout} A new layout, or the same one when nothing changes.
 */
export function setOrderBillable(layout, orderKey, billable) {
    const next = Boolean(billable);
    const order = layout.orders.find((o) => o.key === orderKey);
    if (!order || order.billable === next) return layout;
    return { ...layout, orders: layout.orders.map((o) => (o === order ? { ...o, billable: next } : o)) };
}

/** One `p_children` element per column: item rows, service ids, claim ids, and Billable. Used by `buildSplitVariables`. */
function layoutChildren(layout, items, services, claims) {
    const serviceIds = services ? services.map((service) => service.service_id) : Object.keys(layout.serviceMap || {});
    const claimIds = claims ? claims.map((claim) => claim.claim_id) : Object.keys(layout.claimMap || {});
    const portionCount = new Map();
    layout.lines.forEach((line) => portionCount.set(line.item_id, (portionCount.get(line.item_id) || 0) + 1));
    return layout.orders.map((order) => {
        const held = new Map();
        layout.lines
            .filter((line) => line.orderKey === order.key)
            .forEach((line) => {
                const entry = held.get(line.item_id) || { quantity: 0, portions: 0 };
                held.set(line.item_id, { quantity: entry.quantity + line.quantity, portions: entry.portions + 1 });
            });
        // A child holding every card of a line takes it whole, so a non-whole quantity is never re-sent rounded.
        return {
            items: [...held].map(([itemId, { quantity, portions }]) =>
                portions === portionCount.get(itemId)
                    ? { item_id: itemId }
                    : { item_id: itemId, quantity: roundQuantity(quantity) }
            ),
            services: serviceIds.filter((id) => layout.serviceMap?.[id] === order.key),
            claims: claimIds.filter((id) => layout.claimMap?.[id] === order.key),
            shipper_billable: order.billable !== false,
        };
    });
}

/**
 * The problem to show inside each new-order column in items mode: "Add at least one item" for an empty column and
 * "Needs at least one physical item" for one holding only intangible lines. An empty column only counts once no
 * items are left in the tray, so the opening layout doesn't read as a list of errors.
 *
 * @param {SplitLayout} layout Current layout.
 * @param {Array<{ item_id: string, intangible?: boolean }>|null|undefined} items The parent's item lines from the order read.
 * @returns {Object<string, string>} Message by column key. Columns without a problem have no entry.
 */
export function columnProblems(layout, items) {
    const byId = new Map((items || []).map((item) => [item.item_id, item]));
    const lines = (layout?.lines || []).filter((line) => byId.has(line.item_id));
    const itemsLeft = lines.some((line) => line.orderKey == null);
    const problems = {};
    (layout?.orders || []).forEach((order) => {
        const placed = lines.filter((line) => line.orderKey === order.key);
        if (placed.length === 0) {
            if (!itemsLeft) problems[order.key] = COLUMN_EMPTY_ERROR;
        } else if (!placed.some((line) => isTangible(byId.get(line.item_id)))) {
            problems[order.key] = COLUMN_NEEDS_PHYSICAL_ERROR;
        }
    });
    return problems;
}

/**
 * Whether the split can be submitted, and where the problems are. Valid means: no `split_blockers` and the
 * line-count rule passes; no item, service, or open claim left in the tray; every column holds at least one physical
 * (tangible) line; and the cards of every line add up to that line's quantity.
 *
 * @param {{ layout: SplitLayout, items: Array<{ item_id: string, quantity?: number|string|null, intangible?: boolean }>, services?: Array<{ service_id: string }>, claims?: Array<{ claim_id: string }>, blockers?: string[] }} args
 *   `items`, `services` and `claims` are the parent's rows from the order read. `blockers` are its `split_blockers` codes.
 * @returns {{ valid: boolean, blocked: boolean, itemsProblem: boolean, servicesProblem: boolean, claimsProblem: boolean, itemsLeft: number, servicesLeft: number, claimsLeft: number, columns: Object<string, string>, quantitiesMatch: boolean }}
 *   `itemsProblem`, `servicesProblem` and `claimsProblem` say which board mode has something to fix. `columns` is
 *   `columnProblems`.
 */
export function layoutValidity({ layout, items, services, claims, blockers }) {
    const list = items || [];
    const byId = new Map(list.map((item) => [item.item_id, item]));
    const known = (layout?.lines || []).filter((line) => byId.has(line.item_id));
    const blocked = (blockers || []).length > 0 || lineCountReason(list) !== null;
    const itemsLeft = known.filter((line) => line.orderKey == null).length;
    const servicesLeft = trayStatus(layout, BOARD_MODES.SERVICES, services).count;
    const claimsLeft = trayStatus(layout, BOARD_MODES.CLAIMS, services, claims).count;
    const held = new Map();
    known.forEach((line) => held.set(line.item_id, (held.get(line.item_id) || 0) + line.quantity));
    const quantitiesMatch = list.every(
        (item) => roundQuantity(held.get(item.item_id) || 0) === roundQuantity(numericQuantity(item.quantity))
    );
    const columns = columnProblems(layout, list);
    const everyColumnTangible = (layout?.orders || []).every((order) =>
        known.some((line) => line.orderKey === order.key && isTangible(byId.get(line.item_id)))
    );
    const itemsProblem = itemsLeft > 0 || !everyColumnTangible || !quantitiesMatch;
    const servicesProblem = servicesLeft > 0;
    const claimsProblem = claimsLeft > 0;
    return {
        valid: !blocked && !itemsProblem && !servicesProblem && !claimsProblem,
        blocked,
        itemsProblem,
        servicesProblem,
        claimsProblem,
        itemsLeft,
        servicesLeft,
        claimsLeft,
        columns,
        quantitiesMatch,
    };
}

/**
 * The board mode to show when the user clicks a disabled Split order: items when items mode has a problem, else
 * services, else claims, else null (nothing to switch to, for example when only a blocker stops the split).
 *
 * @param {{ itemsProblem: boolean, servicesProblem: boolean, claimsProblem?: boolean }} validity The result of `layoutValidity`.
 * @returns {'items'|'services'|'claims'|null} A `BOARD_MODES` value, or null.
 */
export function problemMode(validity) {
    if (validity?.itemsProblem) return BOARD_MODES.ITEMS;
    if (validity?.servicesProblem) return BOARD_MODES.SERVICES;
    if (validity?.claimsProblem) return BOARD_MODES.CLAIMS;
    return null;
}

/**
 * Fit the layout to a fresh read of the order, for example after a failed split. Lines, services, and claims no
 * longer on the order (or no longer open) drop out, and services and claims placed on a column no longer on the
 * board go back to the tray. New lines land whole in the tray, and new services and claims stay unplaced. A line
 * whose quantity changed goes back to the tray as one whole card. Columns and their Billable settings are kept.
 *
 * @param {SplitLayout} layout Current layout.
 * @param {Array<{ item_id: string, quantity?: number|string|null }>|null|undefined} items Item lines from the new read.
 * @param {Array<{ service_id: string }>|null|undefined} services Services from the new read.
 * @param {Array<{ claim_id: string }>|null|undefined} [claims] Open claims from the new read.
 * @returns {SplitLayout} The merged layout.
 */
export function mergeRefreshedLayout(layout, items, services, claims) {
    const lines = [];
    (items || []).forEach((item) => {
        const quantity = numericQuantity(item.quantity);
        const portions = layout.lines.filter((line) => line.item_id === item.item_id);
        const held = roundQuantity(portions.reduce((sum, line) => sum + line.quantity, 0));
        if (portions.length > 0 && held === roundQuantity(quantity)) {
            lines.push(...portions);
        } else {
            lines.push({ item_id: item.item_id, quantity, portionKey: item.item_id, orderKey: null });
        }
    });
    // Keep the board's card order: cards still on the order first, in their old order, then the new whole lines.
    const kept = layout.lines.filter((line) => lines.includes(line));
    const added = lines.filter((line) => !layout.lines.includes(line));
    const orderKeys = new Set(layout.orders.map((order) => order.key));
    const keep = (map, rows, idKey) => {
        const ids = new Set((rows || []).map((row) => row[idKey]));
        return Object.fromEntries(Object.entries(map || {}).filter(([id, key]) => ids.has(id) && orderKeys.has(key)));
    };
    return {
        ...layout,
        lines: [...kept, ...added],
        serviceMap: keep(layout.serviceMap, services, 'service_id'),
        claimMap: keep(layout.claimMap, claims, 'claim_id'),
    };
}
